import { Loader2, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  type BulkSyncTagFromCrmResponse,
  useBulkSyncTagFromCrmMutation,
} from '~entities/parent/api/bulk-sync-tag-from-crm';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { useGetGhlLocationTags } from '~shared/api/get-ghl-location-tags';
import Select from '~shared/components/select/select';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';

type CrmTagOption = {
  label: string;
  value: string;
};

type TagSyncPreview = {
  tagId: string;
  tagName: string;
  ghlContactsWithTag: number;
  wouldTag: number;
  alreadyTagged: number;
  noParent: number;
  skippedNoEmail: number;
  ambiguous: number;
};

type BulkSyncTagFromCrmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

const toPreview = (result: BulkSyncTagFromCrmResponse): TagSyncPreview => ({
  tagId: result.tagId,
  tagName: result.tagName,
  ghlContactsWithTag: result.ghlContactsWithTag,
  wouldTag: result.counts.would_tag,
  alreadyTagged: result.counts.already_tagged,
  noParent: result.counts.no_parent,
  skippedNoEmail: result.counts.skipped_no_email,
  ambiguous: result.counts.ambiguous_parents,
});

export const BulkSyncTagFromCrmDialog = ({
  open,
  onOpenChange,
  onSuccess,
}: BulkSyncTagFromCrmDialogProps) => {
  const [selectedTags, setSelectedTags] = useState<Array<CrmTagOption>>([]);
  const [previews, setPreviews] = useState<Array<TagSyncPreview>>([]);

  const { data: studioDetails } = useGetStudioDetails();
  const { data: crmTags, isFetching: isFetchingCrmTags } =
    useGetGhlLocationTags(open);
  const { mutateAsync: bulkSyncTagFromCrm, isPending } =
    useBulkSyncTagFromCrmMutation();

  const tagOptions = useMemo(
    () =>
      (crmTags ?? []).map((tag) => ({
        label: tag.name,
        value: tag.id,
      })),
    [crmTags],
  );

  const resetState = () => {
    setSelectedTags([]);
    setPreviews([]);
  };

  const handlePreview = async () => {
    if (selectedTags.length === 0) {
      toast.error('Please select at least one CRM tag');
      return;
    }
    if (!studioDetails?._id) {
      toast.error('Studio details not available');
      return;
    }

    try {
      const results: Array<TagSyncPreview> = [];
      for (const tag of selectedTags) {
        const result = await bulkSyncTagFromCrm({
          tagId: tag.value,
          tagName: tag.label,
          studioId: studioDetails._id,
          dryRun: true,
        });
        results.push(toPreview(result));
      }
      setPreviews(results);
    } catch (error) {
      console.error(error);
      toast.error('Failed to match CRM contacts');
    }
  };

  const handleApply = async () => {
    if (selectedTags.length === 0) {
      toast.error('Please select at least one CRM tag');
      return;
    }
    if (!studioDetails?._id) {
      toast.error('Studio details not available');
      return;
    }
    if (previews.length === 0) {
      toast.error('Please preview before syncing');
      return;
    }

    try {
      let totalTagged = 0;
      let totalFailed = 0;
      const syncedNames: Array<string> = [];

      for (const tag of selectedTags) {
        const result = await bulkSyncTagFromCrm({
          tagId: tag.value,
          tagName: tag.label,
          studioId: studioDetails._id,
          dryRun: false,
        });
        totalTagged += result.tagged;
        totalFailed += result.failed;
        syncedNames.push(result.tagName);
      }

      if (totalFailed > 0) {
        toast.warning(
          `Synced ${totalTagged} families across ${syncedNames.length} ${
            syncedNames.length === 1 ? 'tag' : 'tags'
          }; ${totalFailed} failed`,
        );
      } else if (syncedNames.length === 1) {
        toast.success(
          `Synced "${syncedNames[0]}" to ${totalTagged} ${
            totalTagged === 1 ? 'family' : 'families'
          }`,
        );
      } else {
        toast.success(
          `Synced ${syncedNames.length} tags to ${totalTagged} ${
            totalTagged === 1 ? 'family' : 'families'
          }`,
        );
      }

      resetState();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error(error);
      toast.error('Failed to sync tag from CRM');
    }
  };

  const previewMatchesSelection =
    previews.length > 0 &&
    previews.length === selectedTags.length &&
    selectedTags.every((tag) => previews.some((p) => p.tagId === tag.value));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetState();
        onOpenChange(next);
      }}
    >
      <DialogContent className='overflow-visible'>
        <DialogHeader>
          <DialogTitle>Sync Tag from CRM</DialogTitle>
          <DialogDescription>
            Pull CRM tags onto matching Studio Portal parents. Matches by
            contact id, then email. Does not create new parents.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label>Tags</Label>
            <Select
              value={selectedTags}
              onChange={(option) => {
                setSelectedTags((option as Array<CrmTagOption> | null) ?? []);
                setPreviews([]);
              }}
              options={tagOptions}
              isMulti
              isLoading={isFetchingCrmTags}
              isClearable
              isDisabled={isPending}
              menuPortalTarget={null}
              placeholder='Select CRM tags'
              noOptionsMessage={() =>
                isFetchingCrmTags ? 'Loading CRM tags...' : 'No CRM tags found'
              }
            />
          </div>
          {previews.length > 0 && (
            <div className='space-y-3'>
              {previews.map((preview) => (
                <div
                  key={preview.tagId}
                  className='rounded-md border p-3 text-sm'
                >
                  <p className='mb-2 font-medium'>{preview.tagName}</p>
                  <ul className='space-y-1 text-muted-foreground'>
                    <li>
                      CRM contacts with tag: {preview.ghlContactsWithTag}
                    </li>
                    <li>Will add to Portal: {preview.wouldTag}</li>
                    <li>Already on Portal: {preview.alreadyTagged}</li>
                    <li>No matching parent: {preview.noParent}</li>
                    <li>Skipped (no email): {preview.skippedNoEmail}</li>
                    <li>Ambiguous matches: {preview.ambiguous}</li>
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className='gap-2 sm:gap-0'>
          <DialogClose asChild>
            <Button variant='outline' disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant='outline'
            onClick={() => void handlePreview()}
            disabled={isPending || selectedTags.length === 0}
          >
            {isPending && <Loader2 className='mr-2 size-4 animate-spin' />}
            Preview
          </Button>
          <Button
            onClick={() => void handleApply()}
            disabled={
              isPending || selectedTags.length === 0 || !previewMatchesSelection
            }
          >
            {isPending && <Loader2 className='mr-2 size-4 animate-spin' />}
            {isPending ? 'Syncing...' : 'Sync from CRM'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BulkSyncTagFromCrmToolbarButton = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <Button variant='outline' size='sm' onClick={onClick}>
    <RefreshCw className='mr-2 size-4' />
    Sync Tag from CRM
  </Button>
);
